<template>
  <!-- Plain wrapper carries the drag handlers: b-card is a functional
       component, so `.native` listeners on it are not reliable. -->
  <div
    class="project-files-wrapper"
    :class="{ 'is-dragging': isDragging }"
    @dragenter.prevent="onDragEnter"
    @dragover.prevent
    @dragleave="onDragLeave"
    @drop.prevent="onDrop"
  >
    <b-card class="project-files-card">
      <div class="d-flex flex-wrap align-items-center justify-content-between">
        <div class="mb-3">
          <h3 class="font-weight-bold mb-1">Project Files</h3>
          <div class="project-files-subtitle">Documents for this project.</div>
          <div
            v-if="readOnly"
            class="project-files-subtitle"
            data-test="read-only-note"
          >
            Shared with you. You can view and download these files.
          </div>
        </div>

        <div v-if="canCreate" class="mb-3">
          <input
            ref="fileInput"
            type="file"
            multiple
            class="d-none"
            :accept="accept"
            @change="onFilesPicked"
          />
          <b-button
            variant="primary"
            :disabled="uploading"
            data-test="upload-button"
            @click="$refs.fileInput.click()"
          >
            <b-spinner v-if="uploading" small class="mr-2 align-middle" />
            {{ uploading ? 'Uploading…' : 'Upload files' }}
          </b-button>
        </div>
      </div>

      <!-- Files | Archived. Archiving hides a file without deleting it. Same
           tabs and count badge as the tradie files list. Archived files are the
           owner's own history, so a read-only recipient never sees the tab. -->
      <b-nav v-if="!readOnly" tabs class="project-files-tabs mt-3 mb-3">
        <b-nav-item
          :active="!showingArchived"
          data-test="tab-active"
          @click="setView('active')"
        >
          Files
          <b-badge
            v-if="counts.active !== null"
            pill
            variant="light"
            class="ml-1"
            >{{ counts.active }}</b-badge
          >
        </b-nav-item>
        <b-nav-item
          :active="showingArchived"
          data-test="tab-archived"
          @click="setView('archived')"
        >
          Archived
          <b-badge
            v-if="counts.archived !== null"
            pill
            variant="light"
            class="ml-1"
            >{{ counts.archived }}</b-badge
          >
        </b-nav-item>
      </b-nav>

      <b-form-input
        v-model="search"
        class="project-files-search mb-3"
        :placeholder="
          showingArchived
            ? 'Search archived files by name or tag'
            : 'Search by name or tag'
        "
        debounce="300"
        data-test="search"
      />

      <!-- Tags in use in this view; click one to filter. -->
      <div
        v-if="tagFilterOptions.length"
        class="project-files-tagbar mb-3"
        data-test="tag-filter"
      >
        <span class="project-files-tagbar__label">Tags</span>
        <button
          v-for="tag in tagFilterOptions"
          :key="tag"
          type="button"
          class="project-tag"
          :class="{ 'is-active': tag === activeTag }"
          :aria-pressed="tag === activeTag ? 'true' : 'false'"
          :data-test="`tag-filter-${tag}`"
          @click="toggleTag(tag)"
        >
          {{ tag }}
        </button>
      </div>

      <b-alert v-if="loadError" show variant="danger" data-test="load-error">
        {{ loadError }}
        <a href="#" class="alert-link ml-2" @click.prevent="load()"
          >Try again</a
        >
      </b-alert>

      <b-overlay :show="loading" rounded="sm">
        <template #overlay>
          <div class="text-center"><digger-loader /></div>
        </template>

        <!-- Desktop: full table (hidden below md) — same shape as the tradie
             files list: Title Case headers, "-" for empty values, pill status
             badges, and a tinted row for anything expired / expiring soon. -->
        <div v-if="!loadError" class="d-none d-md-block">
          <b-table
            responsive
            hover
            :show-empty="!loading"
            :items="files"
            :fields="fields"
            :tbody-tr-class="rowClass"
            class="project-files-table mb-0"
            data-test="files-table"
            @row-clicked="onRowClicked"
          >
            <template #empty>
              <div class="project-files-empty" data-test="empty-state">
                {{ emptyText }}
                <div v-if="emptyHint" class="project-files-empty__hint">
                  {{ emptyHint }}
                </div>
                <div v-if="hasFilters">
                  <a
                    href="#"
                    data-test="clear-filters"
                    @click.prevent="clearFilters"
                    >Clear filters</a
                  >
                </div>
              </div>
            </template>

            <template #cell(name)="{ item }">
              <!-- The whole row opens the file too; this is the keyboard /
                   screen-reader way in. -->
              <button
                type="button"
                class="project-file-name"
                :aria-label="`View details of ${item.name}`"
                data-test="open-overview"
                @click="openOverview(item)"
              >
                {{ item.name }}
              </button>
            </template>

            <template #cell(type)="{ item }">
              {{ typeLabel(item) }}
            </template>

            <template #cell(expires_at)="{ item }">
              <span data-test="expiry-date">{{
                item.expires_at ? dateLabel(item.expires_at) : '-'
              }}</span>
            </template>

            <template #cell(status)="{ item }">
              <span class="project-file-status-cell">
                <b-badge
                  v-if="attentionLevel(item) === 'expired'"
                  v-b-tooltip.hover="expiryTip(item)"
                  pill
                  variant="danger"
                  class="project-file-status-badge"
                  data-test="expiry-badge"
                  >Expired</b-badge
                >
                <b-badge
                  v-else-if="attentionLevel(item) === 'expiring'"
                  v-b-tooltip.hover="expiryTip(item)"
                  pill
                  variant="warning"
                  class="project-file-status-badge project-file-status-badge--soon"
                  data-test="expiry-badge"
                  >Expiring soon</b-badge
                >
                <template v-else>-</template>
              </span>
            </template>

            <template #cell(uploaded_by)="{ item }">
              {{ uploaderName(item) }}
            </template>

            <template #cell(created_at)="{ item }">
              {{ dateLabel(item.created_at) }}
            </template>

            <template #cell(archived_at)="{ item }">
              {{ dateLabel(item.archived_at) }}
            </template>

            <template #cell(actions)="{ item }">
              <!-- Same three-dots menu as the other file screens (FileOptions):
                   a bare kebab, an icon on every item, a divider between them.
                   boundary="window" + fixed positioning lets it escape the
                   table's horizontal-scroll box instead of clipping. -->
              <b-dropdown
                v-if="hasMenuActions"
                no-caret
                variant="transparent"
                right
                boundary="window"
                :popper-opts="{ positionFixed: true }"
                :toggle-attrs="{
                  'aria-label': `More actions for ${item.name}`,
                }"
                toggle-class="project-file-kebab-toggle"
                menu-class="project-file-menu"
                data-test="actions-menu"
              >
                <template #button-content>
                  <!-- Not the global `.kebab` class: that one is
                       position:absolute (built for file cards) and would pull
                       the icon out of the table row. -->
                  <b-icon class="project-file-kebab" icon="three-dots"></b-icon>
                </template>

                <b-dropdown-item
                  v-if="canEdit"
                  class="p-0"
                  data-test="edit-button"
                  @click="openEdit(item)"
                >
                  <b-icon
                    icon="pencil-square"
                    font-scale="0.95"
                    class="mr-3"
                  ></b-icon>
                  <span>Edit</span>
                </b-dropdown-item>
                <b-dropdown-divider v-if="canEdit" />
                <b-dropdown-item
                  v-if="canEdit && !showingArchived"
                  class="p-0"
                  :disabled="busyId === item.id"
                  data-test="archive-button"
                  @click="archive(item)"
                >
                  <b-icon
                    icon="archive"
                    font-scale="0.95"
                    class="mr-3"
                  ></b-icon>
                  <span>Archive</span>
                </b-dropdown-item>
                <b-dropdown-item
                  v-if="canEdit && showingArchived"
                  class="p-0"
                  :disabled="busyId === item.id"
                  data-test="restore-button"
                  @click="restore(item)"
                >
                  <!-- The Archive box with an up arrow where its handle slot is:
                       Bootstrap Icons has no "unarchive". Same size and
                       baseline as the b-icons around it. -->
                  <svg
                    class="b-icon bi mr-3"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 16 16"
                    width="1em"
                    height="1em"
                    fill="currentColor"
                    style="font-size: 95%"
                    aria-hidden="true"
                    focusable="false"
                    data-test="unarchive-icon"
                  >
                    <path
                      d="M0 2a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1v7.5a2.5 2.5 0 0 1-2.5 2.5h-9A2.5 2.5 0 0 1 1 12.5V5a1 1 0 0 1-1-1V2zm2 3v7.5A1.5 1.5 0 0 0 3.5 14h9a1.5 1.5 0 0 0 1.5-1.5V5H2zm13-3H1v2h14V2z"
                    />
                    <path
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1.1"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M8 13V7.6M5.7 9.6 8 7.3l2.3 2.3"
                    />
                  </svg>
                  <span>Unarchive</span>
                </b-dropdown-item>

                <b-dropdown-divider v-if="canDownload && canEdit" />
                <b-dropdown-item
                  v-if="canDownload"
                  class="p-0"
                  :disabled="downloadingId === item.id"
                  data-test="download-button"
                  @click="download(item)"
                >
                  <b-icon
                    icon="download"
                    font-scale="0.95"
                    class="mr-3"
                  ></b-icon>
                  <span>Download</span>
                </b-dropdown-item>

                <b-dropdown-divider
                  v-if="canDelete && (canEdit || canDownload)"
                />
                <b-dropdown-item
                  v-if="canDelete"
                  class="p-0"
                  data-test="delete-button"
                  @click="openDelete(item)"
                >
                  <b-icon icon="trash" font-scale="0.95" class="mr-3"></b-icon>
                  <span>Delete</span>
                </b-dropdown-item>
              </b-dropdown>
            </template>
          </b-table>
        </div>

        <!-- Mobile: stacked file cards (hidden on md+), like the tradie files
             list. Tapping a card opens the file, where the actions live. -->
        <div v-if="!loadError" class="d-md-none mobile-file-list">
          <div
            v-for="item in files"
            :key="item.id"
            class="mobile-file-item"
            :class="mobileAttentionClass(item)"
            data-test="mobile-row"
            @click="openOverview(item)"
          >
            <div class="d-flex align-items-start">
              <div class="flex-grow-1" style="min-width: 0">
                <div class="mobile-file__name">{{ item.name }}</div>
                <div class="mobile-file__meta">
                  <span>
                    <span class="mobile-file__label">Type</span>
                    {{ typeLabel(item) }}
                  </span>
                  <span>
                    <span class="mobile-file__label">Expiry</span>
                    {{ item.expires_at ? dateLabel(item.expires_at) : '-' }}
                  </span>
                  <span>
                    <span class="mobile-file__label">Uploaded by</span>
                    {{ uploaderName(item) }}
                  </span>
                </div>
              </div>
              <div class="mobile-file__status">
                <b-badge
                  v-if="attentionLevel(item) === 'expired'"
                  pill
                  variant="danger"
                  class="project-file-status-badge"
                  >Expired</b-badge
                >
                <b-badge
                  v-else-if="attentionLevel(item) === 'expiring'"
                  pill
                  variant="warning"
                  class="project-file-status-badge project-file-status-badge--soon"
                  >Expiring soon</b-badge
                >
              </div>
            </div>
          </div>
          <div
            v-if="!files.length && !loading"
            class="text-center py-4 text-muted"
            data-test="mobile-empty-state"
          >
            {{ emptyText }}
            <div v-if="hasFilters">
              <a href="#" @click.prevent="clearFilters">Clear filters</a>
            </div>
          </div>
        </div>
      </b-overlay>

      <b-pagination
        v-if="meta.last_page > 1"
        class="mt-3 mb-0"
        :value="meta.current_page"
        :total-rows="meta.total"
        :per-page="meta.per_page"
        align="center"
        @change="onPageChange"
      />

      <!-- Upload details: optional expiry + tags for everything just picked -->
      <b-modal
        v-model="uploadOpen"
        centered
        ok-title="UPLOAD"
        cancel-title="CANCEL"
        :ok-disabled="uploading || !pendingFiles.length"
        data-test="upload-modal"
        @ok.prevent="submitUpload"
        @hidden="onUploadModalHidden"
      >
        <template #modal-title>
          <h3 class="font-weight-bold">Upload Files</h3>
        </template>
        <div class="project-upload-summary mb-4" data-test="upload-summary">
          <strong>{{ pendingFiles.length }}</strong>
          {{ pendingFiles.length === 1 ? 'file' : 'files' }} selected
          <ul class="project-upload-list">
            <li v-for="file in pendingPreview" :key="file.name">
              {{ file.name }}
            </li>
            <li v-if="pendingFiles.length > pendingPreview.length">
              …and {{ pendingFiles.length - pendingPreview.length }} more
            </li>
          </ul>
        </div>

        <b-form-group
          label="Expiry date (optional)"
          label-for="project-file-upload-expiry"
          :description="
            pendingFiles.length > 1
              ? 'Applies to all selected files. You can change each one later.'
              : 'For documents that need renewing, like insurance or a permit.'
          "
        >
          <b-form-datepicker
            id="project-file-upload-expiry"
            v-model="uploadExpiry"
            locale="en-AU"
            :date-format-options="dateFormatOptions"
            :start-weekday="1"
            reset-button
            no-flip
            placeholder="dd/mm/yyyy"
            data-test="upload-expiry"
          />
        </b-form-group>

        <b-form-group
          label="Tags (optional)"
          :description="
            pendingFiles.length > 1
              ? 'Applied to all selected files.'
              : 'Makes files easy to find. For example: swms, jha, insurance.'
          "
        >
          <project-file-tags-input
            v-model="uploadTags"
            :suggestions="tagOptions"
            :show-example="false"
          />
        </b-form-group>
      </b-modal>

      <!-- Edit name, expiry and tags -->
      <b-modal
        v-model="editOpen"
        centered
        ok-title="SAVE"
        cancel-title="CANCEL"
        :ok-disabled="!editValid || saving"
        data-test="edit-modal"
        @ok.prevent="submitEdit"
      >
        <template #modal-title>
          <h3 class="font-weight-bold">Edit File</h3>
        </template>
        <b-form-group label="Name" label-for="project-file-edit-name">
          <b-form-input
            id="project-file-edit-name"
            v-model="editName"
            maxlength="255"
            autofocus
            data-test="edit-name"
            @keyup.enter="submitEdit"
          />
        </b-form-group>

        <!-- Swap the document itself. Any allowed type can replace any other;
             the name, expiry, tags and shares above stay as they are. -->
        <b-form-group label="File" :description="'Allowed: ' + typesHint">
          <input
            ref="editFileInput"
            type="file"
            class="d-none"
            :accept="accept"
            data-test="edit-file-input"
            @change="onEditFilePicked"
          />
          <div class="d-flex align-items-center edit-file-row">
            <div
              class="edit-file-name text-truncate"
              data-test="edit-file-name"
            >
              <template v-if="editNewFile">
                <strong>{{ editNewFile.name }}</strong>
                <span class="text-muted">
                  ({{ formatBytes(editNewFile.size) }}) will replace the current
                  file
                </span>
              </template>
              <template v-else>{{ editCurrentFileLabel }}</template>
            </div>
            <b-button
              v-if="editNewFile"
              size="sm"
              variant="outline-secondary"
              class="ml-2"
              :disabled="saving"
              data-test="edit-file-undo"
              @click="editNewFile = null"
            >
              Undo
            </b-button>
            <b-button
              v-else
              size="sm"
              variant="outline-primary"
              class="ml-2"
              :disabled="saving"
              data-test="edit-file-replace"
              @click="$refs.editFileInput.click()"
            >
              Replace file
            </b-button>
          </div>
        </b-form-group>

        <b-form-group label="Expiry date (optional)">
          <b-form-datepicker
            v-model="editExpiry"
            locale="en-AU"
            :date-format-options="dateFormatOptions"
            :start-weekday="1"
            reset-button
            no-flip
            placeholder="dd/mm/yyyy"
            data-test="edit-expiry"
          />
        </b-form-group>

        <b-form-group label="Tags (optional)">
          <project-file-tags-input
            v-model="editTags"
            :suggestions="tagOptions"
          />
        </b-form-group>

        <div v-if="editError" class="input-error mt-2">{{ editError }}</div>
      </b-modal>

      <!-- Delete (permanent from the user's point of view → danger). Same
           title / wording / button as deleting a tradie's file. -->
      <confirmation-modal
        :showModal="deleteOpen"
        title="File Deletion"
        :message="deleteMessage"
        okLabel="YES, DELETE FILE"
        okVariant="danger"
        @close="deleteOpen = false"
        @ok="confirmDelete"
      />
    </b-card>

    <!-- Click a file to see it: a modal, like viewing a tradie's file. -->
    <project-file-overview
      :visible="overviewOpen"
      :file="overviewFile"
      :projectId="projectId"
      :canDownload="canDownload"
      :canEdit="canEdit"
      :canDelete="canDelete"
      :downloading="!!overviewFile && downloadingId === overviewFile.id"
      :busy="!!overviewFile && busyId === overviewFile.id"
      @close="closeOverview"
      @hidden="onOverviewHidden"
      @download="download"
      @edit="openEdit"
      @archive="onOverviewArchive"
      @restore="onOverviewRestore"
      @delete="openDelete"
      @filter-tag="onOverviewTag"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import dayjs from 'dayjs'
import ConfirmationModal from '@/components/modals/ConfirmationModal'
import ProjectFileTagsInput from '@/components/projects/ProjectFileTagsInput'
import ProjectFileOverview from '@/components/projects/ProjectFileOverview'
import {
  PROJECT_FILE_ACCEPT,
  PROJECT_FILE_TYPES_HINT,
  expiryDescription,
  fileTypeLabel,
  formatBytes,
  normalizeTags,
  splitValidFiles,
  validateProjectFile,
} from '@/utils/projectFiles'

// The store is shared, so only the newest panel may clear it on the way out.
let latestPanel = 0

export default {
  name: 'ProjectFilesPanel',

  components: { ConfirmationModal, ProjectFileTagsInput, ProjectFileOverview },

  props: {
    projectId: { type: [Number, String], required: true },
  },

  data() {
    return {
      accept: PROJECT_FILE_ACCEPT,
      typesHint: PROJECT_FILE_TYPES_HINT,
      // The file overview modal. We keep the last file we showed so it doesn't
      // blank out while fading closed, or if an edit drops it from the current
      // filter.
      overviewOpen: false,
      overviewId: null,
      overviewSnapshot: null,

      // dd/mm/yyyy, matching the rest of the app (native date inputs show US order here).
      dateFormatOptions: { year: 'numeric', month: 'numeric', day: 'numeric' },
      search: '',
      activeTag: '',
      // 'active' (Files tab) | 'archived' (Archived tab)
      view: 'active',
      isDragging: false,
      dragDepth: 0,
      downloadingId: null,
      // Row currently being archived/restored, so the button can't double-fire.
      busyId: null,

      // Upload details step (after picking files, before sending)
      uploadOpen: false,
      pendingFiles: [],
      uploadExpiry: '',
      uploadTags: [],

      editOpen: false,
      editTarget: null,
      editName: '',
      editExpiry: '',
      editTags: [],
      // A replacement document picked in the edit dialog, not yet sent.
      editNewFile: null,
      editError: '',
      saving: false,

      deleteOpen: false,
      deleteTarget: null,
    }
  },

  computed: {
    ...mapGetters('auth', ['hasPermission']),
    ...mapGetters('projectFile', [
      'files',
      'meta',
      'counts',
      'tagOptions',
      'readOnly',
      'loading',
      'loadError',
      'uploading',
    ]),

    showingArchived() {
      return this.view === 'archived'
    },

    // Downloading is allowed for a read-only recipient; everything that
    // changes something is not, whatever their role says.
    canDownload() {
      return this.hasPermission('projects.files.download')
    },
    canCreate() {
      return !this.readOnly && this.hasPermission('projects.files.create')
    },
    canEdit() {
      return !this.readOnly && this.hasPermission('projects.files.edit')
    },
    canDelete() {
      return !this.readOnly && this.hasPermission('projects.files.delete')
    },

    searchTerm() {
      return this.search.trim()
    },

    // The active tag stays selectable even if it has dropped out of the list.
    tagFilterOptions() {
      const options = this.tagOptions.slice()
      if (this.activeTag && options.indexOf(this.activeTag) === -1) {
        options.unshift(this.activeTag)
      }
      return options
    },

    pendingPreview() {
      return this.pendingFiles.slice(0, 5)
    },

    // Title Case labels and a Status column, like the tradie files list
    // ("File Name", "Expiry Date"…). The archived view has no Status (an
    // archived file is no longer being kept current) and shows when it was
    // archived, not when it was uploaded.
    fields() {
      const fields = [
        { key: 'name', label: 'File Name', class: 'w-25' },
        { key: 'type', label: 'Type' },
        { key: 'expires_at', label: 'Expiry Date' },
      ]
      if (!this.showingArchived) fields.push({ key: 'status', label: 'Status' })
      fields.push({ key: 'uploaded_by', label: 'Uploaded By' })
      fields.push(
        this.showingArchived
          ? { key: 'archived_at', label: 'Date Archived' }
          : { key: 'created_at', label: 'Date Uploaded' },
      )
      if (this.hasMenuActions) {
        fields.push({ key: 'actions', label: '', thStyle: { width: '1%' } })
      }
      return fields
    },

    // The three-dots menu (Edit, Archive/Restore, Download, Delete), like
    // FileOptions on the other file screens. Anyone who can do at least one of
    // those gets it.
    hasMenuActions() {
      return this.canDownload || this.canEdit || this.canDelete
    },

    hasFilters() {
      return !!(this.searchTerm || this.activeTag)
    },

    // Empty-table wording follows the tradie files list ("There are no files
    // to show." / "No archived files."), with a match message while filtering.
    emptyText() {
      const archived = this.showingArchived ? 'archived ' : ''
      if (this.searchTerm && this.activeTag) {
        return `No ${archived}files match “${this.searchTerm}” with the tag “${this.activeTag}”.`
      }
      if (this.searchTerm)
        return `No ${archived}files match “${this.searchTerm}”.`
      if (this.activeTag)
        return `No ${archived}files tagged “${this.activeTag}”.`
      return this.showingArchived
        ? 'No archived files.'
        : 'There are no files to show.'
    },

    // A hint on the truly empty screens, only for someone who can act on it.
    emptyHint() {
      if (this.hasFilters) return ''
      // A company the project is shared with sees only what the owner picked.
      if (this.readOnly && !this.showingArchived) {
        return 'The owner has not shared any files from this project with you.'
      }
      if (this.showingArchived) {
        return this.canEdit
          ? 'Archive files you no longer need. They stay here and can be unarchived.'
          : ''
      }
      return this.canCreate
        ? `Drag files here, or use Upload files. ${this.typesHint}.`
        : ''
    },

    editCurrentFileLabel() {
      const file = this.editTarget
      if (!file) return ''
      const label = fileTypeLabel(file.mime_type, file.file_name)
      return [file.file_name, `${label}, ${formatBytes(file.size)}`]
        .filter(Boolean)
        .join(' · ')
    },

    editValid() {
      return this.editName.trim().length > 0
    },

    // The row currently shown in the overview. Read live from the list so an
    // edit (name, tags, expiry) shows up immediately; fall back to the last
    // snapshot if the list no longer contains it.
    overviewFile() {
      const live =
        this.overviewId !== null &&
        this.files.find((f) => f.id === this.overviewId)
      return live || this.overviewSnapshot
    },

    deleteMessage() {
      // Same wording as deleting a tradie's file.
      const name = this.deleteTarget ? this.deleteTarget.name : ''
      return `Are you sure to delete ${name}?`
    },
  },

  watch: {
    projectId() {
      this.closeOverview()
      this.overviewId = null
      this.overviewSnapshot = null
      this.search = ''
      this.activeTag = ''
      this.view = 'active'
      this.load(1)
    },

    // Keep the snapshot fresh so the overview never shows stale details.
    files(list) {
      if (this.overviewId === null) return
      const live = list.find((f) => f.id === this.overviewId)
      if (live) this.overviewSnapshot = live
    },

    searchTerm() {
      this.load(1)
    },
  },

  created() {
    this.panelId = ++latestPanel
    this.reset()
    this.load(1)
  },

  beforeDestroy() {
    // When one project page replaces another, the new panel is created before
    // the old one is destroyed. Resetting then would wipe the list the new
    // panel just asked for and discard its response as stale.
    if (this.panelId === latestPanel) this.reset()
  },

  methods: {
    ...mapActions('projectFile', [
      'fetchFiles',
      'uploadFiles',
      'updateFile',
      'replaceFile',
      'deleteFile',
      'archiveFile',
      'unarchiveFile',
      'getDownloadUrl',
    ]),
    reset() {
      this.$store.commit('projectFile/reset')
    },

    load(page) {
      return this.fetchFiles({
        projectId: this.projectId,
        page: page || this.meta.current_page,
        search: this.searchTerm,
        archived: this.showingArchived,
        tag: this.activeTag,
      })
    },

    setView(view) {
      if (this.view === view) return
      // A file from the other tab isn't in this list any more.
      this.closeOverview()
      this.view = view
      // The tag list is per view, so a tag from the other tab means nothing here.
      this.activeTag = ''
      this.load(1)
    },

    toggleTag(tag) {
      this.activeTag = this.activeTag === tag ? '' : tag
      this.load(1)
    },

    clearFilters() {
      const hadSearch = !!this.searchTerm
      const hadTag = !!this.activeTag
      this.activeTag = ''
      this.search = ''
      // Clearing a real search reloads through its watcher (with the tag
      // already cleared); otherwise there's only the tag to reload for.
      if (!hadSearch && hadTag) this.load(1)
    },

    // Removing the last row on a later page would otherwise leave an empty page.
    async stepBackIfPageEmpty() {
      if (!this.files.length && this.meta.current_page > 1) {
        await this.load(this.meta.current_page - 1)
      }
    },

    onPageChange(page) {
      this.load(page)
    },

    typeLabel(item) {
      return fileTypeLabel(item.mime_type, item.file_name)
    },
    // "-" for nothing, like the rest of the app's tables.
    dateLabel(value) {
      return value ? dayjs(value).format('DD/MM/YYYY') : '-'
    },
    uploaderName(item) {
      return item.uploaded_by && item.uploaded_by.full_name
        ? item.uploaded_by.full_name
        : '-'
    },

    // 'expired' | 'expiring' | null. An archived file is no longer being kept
    // current, so it never raises an alarm.
    attentionLevel(item) {
      if (this.showingArchived) return null
      if (item.expiry_status === 'expired') return 'expired'
      if (item.expiry_status === 'expiring') return 'expiring'
      return null
    },

    // The pill's tooltip: the exact wording ("Expires in 5 days (15/10/2026)").
    expiryTip(item) {
      return expiryDescription(item)
    },

    // Expired / expiring rows are tinted across the whole row, like the tradie
    // files list (red / amber).
    rowClass(item, type) {
      // b-table also asks for a class for its "empty" / "busy" rows, with no
      // file (item is null and type is 'row-empty' etc.) — only real rows get one.
      if (!item || (type && type !== 'row')) return []
      const level = this.attentionLevel(item)
      return [
        'project-file-row',
        'cursor-pointer',
        level ? 'project-file-row-attention' : '',
        level === 'expired' ? 'bg-alert' : '',
        level === 'expiring' ? 'bg-highlight' : '',
      ]
    },

    mobileAttentionClass(item) {
      const level = this.attentionLevel(item)
      if (level === 'expired') return 'mobile-file-item--expired'
      if (level === 'expiring') return 'mobile-file-item--soon'
      return ''
    },

    // ---- file overview (a modal, like viewing a tradie's file) --------------

    // A click anywhere on the row opens the overview — except on a control
    // inside it (the ⋯ menu, Download, a tag chip), which does its own thing.
    onRowClicked(item, index, event) {
      const target = event && event.target
      if (
        target &&
        typeof target.closest === 'function' &&
        target.closest('button, a, input, label, .dropdown, .dropdown-menu')
      ) {
        return
      }
      this.openOverview(item)
    },

    openOverview(item) {
      this.overviewId = item.id
      this.overviewSnapshot = item
      this.overviewOpen = true
    },

    closeOverview() {
      this.overviewOpen = false
    },

    // Forget the file once the modal has finished closing.
    onOverviewHidden() {
      if (!this.overviewOpen) {
        this.overviewId = null
        this.overviewSnapshot = null
      }
    },

    // Archive / Restore remove the file from this list, so the overview closes
    // with it — but only if it worked (on failure the row is still here).
    async onOverviewArchive(file) {
      await this.archive(file)
      if (!this.files.some((f) => f.id === file.id)) this.closeOverview()
    },

    async onOverviewRestore(file) {
      await this.restore(file)
      if (!this.files.some((f) => f.id === file.id)) this.closeOverview()
    },

    // A tag in the overview filters the list by it, then gets out of the way.
    onOverviewTag(tag) {
      this.closeOverview()
      if (this.activeTag !== tag) this.toggleTag(tag)
    },

    toast(message, variant, title) {
      this.$bvToast.toast(message, {
        title: title || (variant === 'danger' ? 'Error' : 'Project files'),
        variant,
        solid: true,
        autoHideDelay: variant === 'danger' ? 8000 : 4000,
      })
    },

    toastFailures(failed) {
      const list = failed
        .slice(0, 5)
        .map((f) => `${f.name}: ${f.message}`)
        .join('\n')
      const more = failed.length > 5 ? `\n…and ${failed.length - 5} more.` : ''
      this.toast(
        `${failed.length} file${
          failed.length === 1 ? '' : 's'
        } couldn’t be uploaded.\n${list}${more}`,
        'danger',
        'Upload problem',
      )
    },

    // ---- upload ---------------------------------------------------------

    onFilesPicked(event) {
      const picked = Array.prototype.slice.call(event.target.files || [])
      // Reset so picking the same file again still fires `change`.
      event.target.value = ''
      this.queueUpload(picked)
    },

    onDragEnter(event) {
      if (!this.canCreate || !this.hasFiles(event)) return
      this.dragDepth++
      this.isDragging = true
    },
    onDragLeave() {
      if (!this.canCreate) return
      this.dragDepth = Math.max(0, this.dragDepth - 1)
      if (this.dragDepth === 0) this.isDragging = false
    },
    onDrop(event) {
      this.dragDepth = 0
      this.isDragging = false
      if (!this.canCreate || !this.hasFiles(event)) return
      this.queueUpload(Array.prototype.slice.call(event.dataTransfer.files))
    },
    hasFiles(event) {
      const types = event.dataTransfer && event.dataTransfer.types
      return !!types && Array.prototype.indexOf.call(types, 'Files') !== -1
    },

    // Step 1: check what was picked, tell the user about anything unusable, and
    // open the details dialog for the rest.
    queueUpload(picked) {
      if (!this.canCreate || this.uploading || !picked.length) return

      const { valid, rejected } = splitValidFiles(picked)
      if (rejected.length) this.toastFailures(rejected)
      if (!valid.length) return

      this.pendingFiles = valid
      this.uploadExpiry = ''
      this.uploadTags = []
      this.uploadOpen = true
    },

    // Step 2: send them with the chosen expiry and tags.
    async submitUpload() {
      if (this.uploading || !this.pendingFiles.length) return
      const files = this.pendingFiles
      const meta = {
        expiresAt: this.uploadExpiry || null,
        tags: normalizeTags(this.uploadTags),
      }
      this.uploadOpen = false
      await this.startUpload(files, meta)
    },

    onUploadModalHidden() {
      this.pendingFiles = []
    },

    async startUpload(picked, meta = {}) {
      if (!this.canCreate || this.uploading || !picked.length) return

      const { valid, rejected } = splitValidFiles(picked)
      let failed = rejected.slice()
      let uploaded = 0

      if (valid.length) {
        const result = await this.uploadFiles({
          projectId: this.projectId,
          files: valid,
          expiresAt: meta.expiresAt || null,
          tags: meta.tags || [],
        })
        uploaded = result.uploaded
        failed = failed.concat(result.failed)
      }

      if (uploaded > 0) {
        this.toast(
          `${uploaded} file${uploaded === 1 ? '' : 's'} uploaded.`,
          'success',
        )
        // New uploads are always active — show them, wherever the user was.
        this.view = 'active'
        this.activeTag = ''
        await this.load(1)
      }

      if (failed.length) this.toastFailures(failed)
    },

    // ---- download -------------------------------------------------------

    async download(item) {
      if (this.downloadingId) return
      this.downloadingId = item.id
      try {
        const { url, file_name: fileName } = await this.getDownloadUrl({
          projectId: this.projectId,
          id: item.id,
        })
        const link = document.createElement('a')
        link.href = url
        link.download = fileName || item.name
        link.rel = 'noopener'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } catch (error) {
        this.toast(
          this.apiMessage(error, 'We couldn’t download that file.'),
          'danger',
        )
      } finally {
        this.downloadingId = null
      }
    },

    // ---- edit -----------------------------------------------------------

    openEdit(item) {
      this.editTarget = item
      this.editName = item.name
      this.editExpiry = item.expires_at || ''
      this.editTags = Array.isArray(item.tags) ? item.tags.slice() : []
      this.editNewFile = null
      this.editError = ''
      this.editOpen = true
    },

    formatBytes,

    onEditFilePicked(event) {
      const picked = event.target.files && event.target.files[0]
      // Reset so picking the same file again still fires `change`.
      event.target.value = ''
      if (!picked) return

      const result = validateProjectFile(picked)
      if (!result.ok) {
        this.editError = `${picked.name}: ${result.message}`
        return
      }
      this.editError = ''
      this.editNewFile = picked
    },

    async submitEdit() {
      if (!this.editValid || this.saving || !this.editTarget) return
      this.saving = true
      this.editError = ''
      try {
        // Replace first: it is the step most likely to be refused (type, size),
        // and nothing else has changed yet if it is.
        if (this.editNewFile) {
          await this.replaceFile({
            projectId: this.projectId,
            id: this.editTarget.id,
            file: this.editNewFile,
          })
        }
        await this.updateFile({
          projectId: this.projectId,
          id: this.editTarget.id,
          name: this.editName.trim(),
          // Empty picker = no expiry: send null so the API clears it.
          expiresAt: this.editExpiry || null,
          tags: normalizeTags(this.editTags),
        })
        this.editOpen = false
        // Tags and expiry can change what belongs in this view and its tag list.
        await this.load()
      } catch (error) {
        this.editError = this.apiMessage(
          error,
          'We couldn’t save those changes.',
        )
      } finally {
        this.saving = false
      }
    },

    // ---- archive / restore ----------------------------------------------

    async archive(item) {
      if (this.busyId) return
      this.busyId = item.id
      try {
        await this.archiveFile({
          projectId: this.projectId,
          id: item.id,
          expired: item.expiry_status === 'expired',
        })
        this.toast(
          `“${item.name}” archived. Find it under Archived.`,
          'success',
        )
        await this.stepBackIfPageEmpty()
      } catch (error) {
        this.toast(
          this.apiMessage(error, 'We couldn’t archive that file.'),
          'danger',
        )
      } finally {
        this.busyId = null
      }
    },

    async restore(item) {
      if (this.busyId) return
      this.busyId = item.id
      try {
        await this.unarchiveFile({
          projectId: this.projectId,
          id: item.id,
          expired: item.expiry_status === 'expired',
        })
        this.toast(`“${item.name}” unarchived.`, 'success')
        await this.stepBackIfPageEmpty()
      } catch (error) {
        this.toast(
          this.apiMessage(error, 'We couldn’t unarchive that file.'),
          'danger',
        )
      } finally {
        this.busyId = null
      }
    },

    // ---- delete ---------------------------------------------------------

    openDelete(item) {
      this.deleteTarget = item
      this.deleteOpen = true
    },

    async confirmDelete() {
      const target = this.deleteTarget
      if (!target) return
      try {
        await this.deleteFile({
          projectId: this.projectId,
          id: target.id,
          archived: this.showingArchived,
          expired: target.expiry_status === 'expired',
        })
        this.toast('File deleted.', 'success')
        // Deleted while its overview was open: nothing left to show.
        if (this.overviewId === target.id) this.closeOverview()
        await this.stepBackIfPageEmpty()
      } catch (error) {
        this.toast(
          this.apiMessage(error, 'We couldn’t delete that file.'),
          'danger',
        )
      } finally {
        this.deleteTarget = null
      }
    },

    apiMessage(error, fallback) {
      const data = error && error.response && error.response.data
      if (data && data.errors) {
        const first = Object.values(data.errors)[0]
        if (Array.isArray(first) && first[0]) return first[0]
      }
      return (data && data.message) || fallback
    },
  },
}
</script>

<style lang="scss" scoped>
// Current / replacement file in the edit dialog: the name takes the free width
// and truncates so a long file name can't push the button off the row.
.edit-file-row {
  min-width: 0;
}

.edit-file-name {
  flex: 1 1 auto;
  min-width: 0;
}

.project-files-card {
  border-radius: 8px;
  transition: border-color 0.15s ease;

  // Whole card is the drop target; the border is the only affordance
  // (DESIGN.md: border colour change, no added shadow).
  .is-dragging > & {
    border-color: #2c66d5;
    border-style: dashed;
  }
}

.project-files-subtitle {
  color: #6b7280;
  font-size: 0.875rem;
}

.project-files-search {
  max-width: 360px;
}

.project-files-tagbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}

.project-files-tagbar__label {
  margin-right: 2px;
  color: #6b7280;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

// Tag chip — a button so it's keyboard-reachable; same look in the filter bar
// and under a file name. Active = brand blue ($brand #006CFF).
.project-tag {
  display: inline-block;
  padding: 0 0.6rem;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  background: #f3f4f6;
  color: #374151;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.7;
  cursor: pointer;

  &:hover {
    border-color: #2c66d5;
  }

  &.is-active {
    border-color: #006cff;
    background: #006cff;
    color: #fff;
  }
}

.project-file-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 6px;
}

// The name is a real button (keyboard + screen readers) that reads as plain
// table text, like the tradie files list.
.project-file-name {
  display: block;
  max-width: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  word-break: break-word;
  cursor: pointer;

  &:hover,
  &:focus {
    outline: none;
    text-decoration: underline;
  }
}

.project-upload-summary {
  padding: 12px 16px;
  background: #f5f6f8;
  border-radius: 8px;
}

.project-upload-list {
  margin: 6px 0 0;
  padding-left: 1.25rem;
  color: #374151;
  font-size: 0.875rem;
  word-break: break-all;
}

.project-files-empty {
  padding: 8px 0;
  text-align: center;
  color: #6c757d;
}

.project-files-empty__hint {
  margin-top: 4px;
  font-size: 0.875rem;
}

// Status pills — exactly the tradie files list's ("Expired" red, "Expiring
// soon" yellow with a yellow border).
.project-file-status-cell {
  display: inline-block;
}

.project-file-status-badge {
  font-weight: 600;
}

.project-file-status-badge--soon {
  background-color: #fff3cd !important;
  color: #856404 !important;
  border: 1px solid #ffc107;
}

.project-files-table {
  // Whole-row tint for anything expired / expiring soon, and a pointer since a
  // row click opens the file (same colours as the tradie files list).
  /deep/ tr.project-file-row {
    cursor: pointer;
  }

  /deep/ tr.project-file-row-attention.bg-alert,
  /deep/ tr.project-file-row-attention.bg-alert:hover {
    background-color: #ffeaea !important;
  }

  /deep/ tr.project-file-row-attention.bg-highlight,
  /deep/ tr.project-file-row-attention.bg-highlight:hover {
    background-color: #fff8e1 !important;
  }

  // The kebab: bare icon, no border; the global .btn is 56px tall. Same size
  // and colour as the global .kebab (24px, #3f4858), but in flow.
  /deep/ .project-file-kebab-toggle.btn {
    height: auto;
    padding: 0.25rem 0.5rem;
    line-height: 1;
  }

  /deep/ .project-file-kebab {
    width: 24px;
    height: 24px;
    color: #3f4858;
  }
}

/* ---------------------------------------------------------------------------
 * Mobile file cards — below md the table is replaced by compact stacked cards
 * (one file per card) so nothing scrolls sideways. Same as the tradie files
 * list.
 * ------------------------------------------------------------------------- */
.mobile-file-list {
  border-top: 1px solid #e1e6ea;
}

.mobile-file-item {
  padding: 0.65rem 0.25rem;
  border-bottom: 1px solid #e1e6ea;
  background: white;
  cursor: pointer;
}

.mobile-file-item--expired {
  background-color: #ffeaea;
}

.mobile-file-item--soon {
  background-color: #fff8e1;
}

.mobile-file__name {
  font-weight: 600;
  font-size: 0.9rem;
  color: #364052;
  line-height: 1.3;
  word-break: break-word;
}

.mobile-file__meta {
  font-size: 0.75rem;
  color: #8b95a3;
  margin-top: 0.2rem;
  line-height: 1.5;
  display: flex;
  flex-direction: column;
}

.mobile-file__label {
  font-weight: 600;
  color: #6c7581;
  margin-right: 0.15rem;
}

.mobile-file__status {
  flex-shrink: 0;
  margin-left: 0.5rem;
  text-align: right;
}
</style>
